import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import AOS from "aos";
import "aos/dist/aos.css";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";

import Home from "./Home/Home.jsx";
import About from "./pages/AboutSection.jsx";
import SignGraphics from "./pages/SignGraphics";
import Catalogue from "./pages/Catalogue";
import Contact from "./pages/Contact";
import ScrollToTop from "./components/ScrollToTop";
import Quote from "./pages/Quote";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Wishlist from "./pages/Wishlist";
import Products from "./pages/Products";
import ProductDetails from "./pages/ProductDetails";
import AdminProducts from "./pages/AdminProducts";
import AdminProductForm from "./pages/AdminProductForm";
import OrderTracking from "./pages/OrderTracking";
import MyOrders from "./pages/MyOrders";
import AdminCategories from "./pages/AdminCategories";
import GuestRoute from "./components/GuestRoute.jsx";
import RoleRoute from "./components/RoleRoute.jsx";
import PublicLayout from "./layouts/PublicLayout.jsx";
import DashboardLayout from "./layouts/DashboardLayout.jsx";
import DashboardHome from "./pages/dashboard/DashboardHome";
import ManageOrders from "./pages/dashboard/ManageOrders";
import ManageQuotes from "./pages/dashboard/ManageQuotes";
import ManageEnquiries from "./pages/dashboard/ManageEnquiries";
import ManageReviews from "./pages/dashboard/ManageReviews";
import ManageUsers from "./pages/dashboard/ManageUsers";
import AdminHomeContent from "./pages/dashboard/AdminHomeContent";
import AdminSignContent from "./pages/dashboard/AdminSignContent";
import AdminCatalogueContent from "./pages/dashboard/AdminCatalogueContent";
import AdminEmailSettings from "./pages/dashboard/AdminEmailSettings";
import NotFound from "./pages/NotFound.jsx";

function App() {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
      easing: "ease-in-out",
    });
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <WishlistProvider>
        <Router>
        <ScrollToTop />

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
        />

        <Routes>
          {/* ===== Public storefront ===== */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/sign-graphics" element={<SignGraphics />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/quote" element={<Quote />} />
            <Route
              path="/login"
              element={
                <GuestRoute>
                  <Login />
                </GuestRoute>
              }
            />
            <Route
              path="/register"
              element={
                <GuestRoute>
                  <Register />
                </GuestRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <GuestRoute>
                  <ForgotPassword />
                </GuestRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <GuestRoute>
                  <ResetPassword />
                </GuestRoute>
              }
            />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/products" element={<Products />} />
            <Route path="/shop" element={<Products />} />
            <Route path="/product/:slug" element={<ProductDetails />} />
            <Route path="/order-tracking" element={<OrderTracking />} />
            <Route path="/orders" element={<MyOrders />} />
             <Route path="*" element={<NotFound />} />
          </Route>

          {/* ===== CMS / control panel ===== */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="orders" element={<MyOrders />} />
            <Route path="track" element={<OrderTracking />} />
            <Route path="wishlist" element={<Wishlist />} />
            <Route path="cart" element={<Cart />} />

            <Route
              path="manage/orders"
              element={
                <RoleRoute minRole="sales">
                  <ManageOrders />
                </RoleRoute>
              }
            />
            <Route
              path="manage/quotes"
              element={
                <RoleRoute minRole="sales">
                  <ManageQuotes />
                </RoleRoute>
              }
            />
            <Route
              path="manage/enquiries"
              element={
                <RoleRoute minRole="sales">
                  <ManageEnquiries />
                </RoleRoute>
              }
            />

            <Route
              path="reviews"
              element={
                <RoleRoute minRole="admin">
                  <ManageReviews />
                </RoleRoute>
              }
            />

            <Route
              path="products"
              element={
                <RoleRoute minRole="admin">
                  <AdminProducts />
                </RoleRoute>
              }
            />
            <Route
              path="products/new"
              element={
                <RoleRoute minRole="admin">
                  <AdminProductForm />
                </RoleRoute>
              }
            />
            <Route
              path="products/:id/edit"
              element={
                <RoleRoute minRole="admin">
                  <AdminProductForm />
                </RoleRoute>
              }
            />
            <Route
              path="categories"
              element={
                <RoleRoute minRole="admin">
                  <AdminCategories />
                </RoleRoute>
              }
            />
            <Route
              path="home"
              element={
                <RoleRoute minRole="admin">
                  <AdminHomeContent />
                </RoleRoute>
              }
            />
            <Route
              path="sign-graphics"
              element={
                <RoleRoute minRole="admin">
                  <AdminSignContent />
                </RoleRoute>
              }
            />
            <Route
              path="catalogue"
              element={
                <RoleRoute minRole="admin">
                  <AdminCatalogueContent />
                </RoleRoute>
              }
            />
            <Route
              path="settings/email"
              element={
                <RoleRoute minRole="admin">
                  <AdminEmailSettings />
                </RoleRoute>
              }
            />
            <Route
              path="users"
              element={
                <RoleRoute minRole="admin">
                  <ManageUsers />
                </RoleRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Legacy admin URLs */}
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/admin/products"
            element={<Navigate to="/dashboard/products" replace />}
          />
          <Route
            path="/admin/products/new"
            element={<Navigate to="/dashboard/products/new" replace />}
          />
          <Route
            path="/admin/categories"
            element={<Navigate to="/dashboard/categories" replace />}
          />
        </Routes>
      </Router>
        </WishlistProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
